/**
 * 일정 스마트 등록 JSON → 상품 일정표 생성 (재사용 서비스).
 *
 * 대리점(hanmaeum)이 "일정 스마트 등록"으로 파싱한 structuredItinerary JSON 을,
 * 공급사가 공개 견적 요청에 참여·상담 저장할 때 생성한 상품(productNo)에
 * 일정 카드/내용/식사로 밀어넣는다.
 *
 * 이 로직은 src/components/SmartItineraryRegisterModal.js 의 JSON 모드 writer
 * (핵심 일정 카드 저장 루프)를 서비스로 이식한 것이다.
 *
 * phase-2: 항공/호텔 전용 섹션 연동 추가.
 *   - 항공: schedule.flight(편명) → 공급사 항공스케줄 조회로 출발/도착 공항·시각 보강
 *           → 항공여정(RCT_T_상품일정항공여정) 섹션에 저장.
 *   - 호텔: jsonLineRoles 의 'hotel' 역할 라인 → 일차별 수집 후 상품 요금제 전체에
 *           동일 호텔을 일괄 등록(RCT_T_상품일정숙박_02) → 호텔 섹션.
 *   항공/호텔 저장 실패는 일정 카드 저장을 되돌리지 않는다(부분 성공 허용, 재시도 가능).
 *
 * structuredItinerary 형태(일차별):
 *   { day:"1일차", meals:["조식","중식","석식"],
 *     locations:[ { place:"장소명",
 *                   schedule:[ { time:"09:00", flight:"KE123", details:["줄1","줄2"] } ] } ] }
 */
import {
  getItineraryType,
  addItinerarySchedule,
  saveItineraryContent,
  saveItineraryMeal,
  getItineraryDetail,
  getFlightScheduleByFlightNo,
  saveFlightItinerary,
  saveHotelItineraryEdits,
  getTourProductResourceList,
  getTourRatePlanList02,
} from './apiService';

/** 태그 제거 + 공백 정리 (이미 대리점에서 sanitize 되지만 방어적으로 한 번 더) */
const stripHtmlToText = (v) => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

const getDayNumber = (dayLabel, index) => {
  if (dayLabel) {
    const m = String(dayLabel).match(/\d+/);
    if (m) return Number(m[0]);
  }
  return index + 1;
};

// --- 호텔 라인 감지/정규화 (hanmaeum isHotelDetailLine 과 동일 규칙) ---
// AI 일정 생성 결과의 호텔은 "HOTEL : 더 스코솔 또는 동급" 처럼 정규화된 라벨로 나온다.
// 역할 태그(jsonLineRoles)가 체인을 거치며 유실되어도, 이 패턴으로 직접 감지해 호텔 섹션으로 보낸다.
const HOTEL_LABEL_RE = /^\s*(?:hotel|호텔|숙박|숙소|htl)\s*[:：]/i;
const isHotelDetailLine = (text) => HOTEL_LABEL_RE.test(String(text ?? ''));
/** "HOTEL : " 라벨만 떼고 순수 호텔명만 남긴다(이름 자체는 가공하지 않음). */
const stripHotelLabel = (text) => String(text ?? '').replace(HOTEL_LABEL_RE, '').trim();

// --- 항공 편명/시간 헬퍼 (SmartItineraryRegisterModal.js 와 동일 규칙) ---
const normalizeFlightNo = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[\s\-]+/g, '')
    .trim();

/** 자유 텍스트에서 편명 패턴(항공사코드 2자 + 편번호 3~4자리)을 추출 */
const extractFlightNo = (value) => {
  const raw = String(value || '').toUpperCase();
  if (!raw) return '';
  const m = raw.match(/([A-Z0-9]{2})\s*[-]?\s*(\d{3,4})/);
  if (!m) return '';
  return normalizeFlightNo(`${m[1]}${m[2]}`);
};

const isFlightNoLike = (value) => {
  const v = normalizeFlightNo(value);
  if (!v) return false;
  return /^[A-Z0-9]{2}\d{3,4}$/.test(v);
};

/** "1025" → "10:25", "10:25" → "10:25" */
const autoFormatTime = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/**
 * @returns {Promise<{days:number, cards:number, flights:number, hotels:number}>}
 *   생성한 일차 수 / 카드 수 / 항공여정 항목 수 / 호텔 등록 일차 수
 * @throws 일정타입번호 조회 실패 등 치명적 오류
 */
export const registerItineraryFromJson = async ({
  productNo,
  companyNo,
  structuredItinerary,
  jsonLineRoles = {},
  userNo,
}) => {
  const itinerary = Array.isArray(structuredItinerary) ? structuredItinerary : [];
  if (!productNo) throw new Error('상품번호가 없어 일정표를 생성할 수 없습니다.');
  if (!itinerary.length) return { days: 0, cards: 0, flights: 0, hotels: 0 };

  // 상품의 일정타입번호 조회 (상품 생성 시 소요시간 기반으로 자동 생성됨)
  const typeResp = await getItineraryType({ productNo });
  const itineraryTypeNo =
    Array.isArray(typeResp?.table) && typeResp.table.length
      ? typeResp.table[0]?.일정타입번호
      : null;
  if (!itineraryTypeNo) throw new Error('일정타입번호를 찾을 수 없습니다.');

  // 일차별 직전 카드번호(앵커) — 새 카드가 항상 그 뒤에 붙도록 함
  const lastScheduleNoByDay = {};
  let cardCount = 0;

  // 호텔 정보를 일차별로 수집 (일정 저장 완료 후 일괄 저장)
  const pendingHotels = {}; // { dayNo: [hotelName1, hotelName2, ...] }

  // 항공 정보 수집 (일차+편명 단위 중복 제거)
  const flightCandidates = []; // [{ dayNo, flightNo, time }]
  const uniqueFlightNos = new Set();
  const flightSeenByDay = new Set();

  for (let dIdx = 0; dIdx < itinerary.length; dIdx += 1) {
    const dayBlock = itinerary[dIdx];
    const dayNo = getDayNumber(dayBlock?.day, dIdx);
    const locations = Array.isArray(dayBlock?.locations) ? dayBlock.locations : [];

    let currentTitle = '';
    let currentDetails = [];

    const flushCard = async () => {
      if (!currentTitle) return;
      const contentText = currentDetails.slice().join('\n\n');
      const selectedScheduleNo = lastScheduleNoByDay[dayNo] || '';
      const addDs = await addItinerarySchedule({
        productNo,
        itineraryTypeNo,
        day: dayNo,
        productTypeCode: undefined,
        name: currentTitle,
        selectedScheduleNo,
      });
      const newNo = Number(addDs?.table?.[0]?.상품일정번호 || 0);
      if (!newNo) throw new Error('신규 일정 번호를 가져오지 못했습니다.');
      lastScheduleNoByDay[dayNo] = newNo;
      await saveItineraryContent({
        scheduleNo: newNo,
        itineraryTypeNo,
        productNo,
        name: currentTitle,
        content: contentText,
        userNo,
      });
      cardCount += 1;
      currentTitle = '';
      currentDetails = [];
    };

    for (let lIdx = 0; lIdx < locations.length; lIdx += 1) {
      const loc = locations[lIdx];
      const placeName = stripHtmlToText(loc?.place);
      if (!placeName) continue;

      const placeRole = jsonLineRoles[`place-${dIdx}-${lIdx}`] || 'title';
      if (placeRole === 'title') {
        await flushCard();
        currentTitle = placeName;
      } else if (placeRole === 'content') {
        if (!currentTitle) currentTitle = placeName;
        else currentDetails.push(placeName);
      } else if (placeRole === 'hotel') {
        // 호텔 역할: 라벨을 뗀 순수 호텔명만 수집(카드에는 넣지 않음)
        const hotelName = stripHotelLabel(placeName);
        if (hotelName) {
          if (!pendingHotels[dayNo]) pendingHotels[dayNo] = [];
          pendingHotels[dayNo].push(hotelName);
        }
      }
      // 'ignore' 는 아무 것도 하지 않음

      const schedules = Array.isArray(loc?.schedule) ? loc.schedule : [];
      for (let sIdx = 0; sIdx < schedules.length; sIdx += 1) {
        const sch = schedules[sIdx];
        const detailsArr = Array.isArray(sch?.details) ? sch.details : [];
        if (!detailsArr.length) continue;
        const time = stripHtmlToText(sch?.time);
        const flight = stripHtmlToText(sch?.flight);

        // 편명이 인식되면 항공 섹션 후보로 수집(일차+편명 중복 제거).
        // 카드 본문에도 편명/시간은 그대로 남겨 정보 손실이 없도록 한다.
        const normalizedFlight = extractFlightNo(flight);
        if (normalizedFlight && isFlightNoLike(normalizedFlight)) {
          const fKey = `${dayNo}-${normalizedFlight}`;
          if (!flightSeenByDay.has(fKey)) {
            flightSeenByDay.add(fKey);
            uniqueFlightNos.add(normalizedFlight);
            flightCandidates.push({ dayNo, flightNo: normalizedFlight, time });
          }
        }

        const prefix = time && flight ? `${time} ${flight}` : time || flight || '';

        for (let dtIdx = 0; dtIdx < detailsArr.length; dtIdx += 1) {
          const body = stripHtmlToText(detailsArr[dtIdx]);
          if (!body) continue;
          const fullLine = prefix ? `${prefix} ${body}` : body;
          let role = jsonLineRoles[`d-${dIdx}-${lIdx}-${sIdx}-${dtIdx}`] || 'content';
          // 역할 태그가 없거나 content 로 온 호텔 라인("HOTEL : …")은 호텔로 자동 분류한다.
          if (role === 'content' && isHotelDetailLine(body)) role = 'hotel';
          if (role === 'ignore') continue;
          if (role === 'title') {
            await flushCard();
            currentTitle = fullLine;
            currentDetails = [];
            continue;
          }
          if (role === 'hotel') {
            // 호텔 역할: 카드에서 빼고, "HOTEL :" 라벨을 뗀 순수 호텔명만 호텔 섹션으로 수집
            const hotelName = stripHotelLabel(body);
            if (hotelName) {
              if (!pendingHotels[dayNo]) pendingHotels[dayNo] = [];
              pendingHotels[dayNo].push(hotelName);
            }
            continue;
          }
          // content
          if (!currentTitle) currentTitle = placeName || body;
          currentDetails.push(fullLine);
        }
      }
    }

    await flushCard();

    // 식사(조식/중식/석식)
    const meals = Array.isArray(dayBlock?.meals) ? dayBlock.meals : [];
    const mealTypes = ['조식', '중식', '석식'];
    for (let mi = 0; mi < 3; mi += 1) {
      const mealContent = String(meals[mi] || '').trim();
      if (!mealContent) continue;
      try {
        await saveItineraryMeal({
          productNo,
          itineraryTypeNo,
          dayNo,
          planLabel: mealTypes[mi],
          oldContent: '',
          newContent: mealContent,
        });
      } catch (e) {
        console.error(`${dayNo}일차 ${mealTypes[mi]} 저장 실패:`, e);
      }
    }
  }

  // --- 항공여정 섹션 저장 ---
  // 상품 상세(getItineraryDetail)를 한 번 조회해 항공/호텔 양쪽에서 재사용한다.
  let itineraryDetailData = null;
  const flightCount = await saveFlightSection({
    productNo,
    itineraryTypeNo,
    flightCandidates,
    uniqueFlightNos,
    getDetail: async () => {
      if (itineraryDetailData === null) {
        try {
          itineraryDetailData = await getItineraryDetail({ productNo, itineraryTypeNo });
        } catch {
          itineraryDetailData = false; // 조회 실패 표식(재시도 방지)
        }
      }
      return itineraryDetailData || null;
    },
  });

  // --- 호텔 섹션 저장 ---
  const hotelCount = await saveHotelSection({
    productNo,
    companyNo,
    itineraryTypeNo,
    pendingHotels,
    getDetail: async () => {
      if (itineraryDetailData === null) {
        try {
          itineraryDetailData = await getItineraryDetail({ productNo, itineraryTypeNo });
        } catch {
          itineraryDetailData = false;
        }
      }
      return itineraryDetailData || null;
    },
  });

  return { days: itinerary.length, cards: cardCount, flights: flightCount, hotels: hotelCount };
};

/**
 * 수집된 편명 후보를 공급사 항공스케줄로 보강하여 항공여정 섹션에 저장한다.
 * 기존 항공여정이 있으면 (일차/편명 or 출발·도착공항) 기준으로 병합한다.
 * @returns {Promise<number>} 저장된 항공여정 항목 수
 */
const saveFlightSection = async ({
  productNo,
  itineraryTypeNo,
  flightCandidates,
  uniqueFlightNos,
  getDetail,
}) => {
  if (!uniqueFlightNos.size) return 0;

  // 편명별 스케줄 조회(부분 실패 무시)
  const flightCacheByNo = {};
  await Promise.all(
    Array.from(uniqueFlightNos).map(async (flightNo) => {
      try {
        flightCacheByNo[flightNo] = await getFlightScheduleByFlightNo({ flightNo });
      } catch {
        flightCacheByNo[flightNo] = null;
      }
    })
  );

  const pendingFlights = [];
  for (const c of flightCandidates) {
    try {
      const ds = flightCacheByNo[c.flightNo];
      const rows = Array.isArray(ds?.table) ? ds.table : [];
      const first = rows.length ? rows[0] : null;
      const depCode = String(first?.출발공항 || '').trim().toUpperCase();
      const arrCode = String(first?.도착공항 || '').trim().toUpperCase();
      const depTimeRaw = String(first?.출발시간 || '').trim();
      const arrTimeRaw = String(first?.도착시간 || '').trim();
      const depTime = depTimeRaw ? autoFormatTime(depTimeRaw) : c.time ? autoFormatTime(c.time) : '';
      const arrTime = arrTimeRaw ? autoFormatTime(arrTimeRaw) : '';
      // 출발/도착 공항을 못 찾으면 항공여정 행을 만들지 않는다(카드 텍스트로는 이미 보존됨)
      if (depCode && arrCode) {
        pendingFlights.push({
          일차: Number(c.dayNo),
          순서: 0,
          편명: c.flightNo,
          출발공항: depCode,
          도착공항: arrCode,
          구분: '217001',
          출발시간: depTime,
          도착시간: arrTime,
          소요시간: '',
        });
      }
    } catch {
      /* skip */
    }
  }

  if (!pendingFlights.length) return 0;

  // 기존 항공여정 로드(있으면 병합)
  let existingFlightItinerary = [];
  const detail = await getDetail();
  if (detail) {
    const tables = ['table', 'table1', 'table2', 'table3', 'table4', 'table5'];
    for (const tableName of tables) {
      const table = detail?.[tableName];
      if (!Array.isArray(table) || !table.length) continue;
      const firstRow = table[0];
      if (!firstRow || !firstRow.항공여정) continue;
      try {
        const parsed = JSON.parse(firstRow.항공여정);
        if (Array.isArray(parsed)) {
          existingFlightItinerary = parsed.map((item, index) => ({
            일차: item.일차 || 1,
            순서: item.순서 || index + 1,
            편명: item.편명 || '',
            출발공항: item.출발공항코드 || item.출발공항 || '',
            도착공항: item.도착공항코드 || item.도착공항 || '',
            구분: item.구분 || '217001',
            출발시간: item.출발시간 || '',
            도착시간: item.도착시간 || '',
            소요시간: item.소요시간 || '',
          }));
          break;
        }
      } catch {
        /* skip */
      }
    }
  }

  const maxSeqByDay = {};
  existingFlightItinerary.forEach((x) => {
    const d = Number(x.일차) || 1;
    const s = Number(x.순서) || 0;
    maxSeqByDay[d] = Math.max(Number(maxSeqByDay[d] || 0), s);
  });

  const findMatchIdx = (arr, fd) => {
    const d = Number(fd.일차);
    const fno = String(fd.편명 || '').trim().toUpperCase();
    const dep = String(fd.출발공항 || '').trim().toUpperCase();
    const arrCode = String(fd.도착공항 || '').trim().toUpperCase();
    return arr.findIndex(
      (x) =>
        Number(x.일차) === d &&
        ((String(x.편명 || '').trim().toUpperCase() === fno && !!fno) ||
          (String(x.출발공항 || '').trim().toUpperCase() === dep &&
            String(x.도착공항 || '').trim().toUpperCase() === arrCode &&
            !!dep &&
            !!arrCode))
    );
  };

  for (const nf of pendingFlights) {
    const idx = findMatchIdx(existingFlightItinerary, nf);
    if (idx >= 0) {
      const keepSeq = existingFlightItinerary[idx].순서 || idx + 1;
      existingFlightItinerary[idx] = { ...existingFlightItinerary[idx], ...nf, 순서: keepSeq };
    } else {
      const d = Number(nf.일차) || 1;
      const nextSeq = Number(maxSeqByDay[d] || 0) + 1;
      maxSeqByDay[d] = nextSeq;
      existingFlightItinerary.push({ ...nf, 순서: nextSeq });
    }
  }

  existingFlightItinerary.sort((a, b) => {
    const d = Number(a.일차) - Number(b.일차);
    return d !== 0 ? d : Number(a.순서) - Number(b.순서);
  });

  try {
    await saveFlightItinerary({ productNo, itineraryTypeNo, flightItinerary: existingFlightItinerary });
  } catch (e) {
    console.error('항공여정 저장 실패:', e);
    return 0;
  }
  return pendingFlights.length;
};

/**
 * 일차별로 수집된 호텔을 상품의 모든 요금제에 일괄 등록한다.
 * 요금제 목록을 조회하지 못하면 기본요금제(1)로 폴백한다.
 * @returns {Promise<number>} 호텔을 등록한 일차 수
 */
const saveHotelSection = async ({ productNo, companyNo, itineraryTypeNo, pendingHotels, getDetail }) => {
  const hotelDays = Object.keys(pendingHotels);
  if (!hotelDays.length) return 0;

  // 1) 상품 요금제 목록을 자원(resource) 단위로 조회
  const allPlanEntries = []; // [{ planNo, planName, resourceNo }]
  if (companyNo) {
    try {
      const resources = await getTourProductResourceList({ companyNo, productNo });
      const resourceList = Array.isArray(resources) ? resources : [];
      const planResults = await Promise.all(
        resourceList.map(async (r) => {
          const resourceNo = r?.자원번호;
          if (!resourceNo) return [];
          try {
            const planRows = await getTourRatePlanList02({ companyNo, productNo, resourceNo });
            const rows = Array.isArray(planRows) ? planRows : [];
            return rows
              .map((pl) => ({
                planNo: pl?.가격옵션번호,
                planName:
                  (typeof pl?.요금제명 === 'string' ? pl.요금제명.trim() : '') ||
                  String(pl?.가격옵션번호 ?? ''),
                resourceNo,
              }))
              .filter((p) => p.planNo != null && String(p.planNo).trim() !== '');
          } catch (e) {
            console.warn(`자원 ${resourceNo} 요금제 조회 실패`, e);
            return [];
          }
        })
      );
      planResults.forEach((arr) => arr.forEach((p) => allPlanEntries.push(p)));
    } catch (e) {
      console.warn('상품 요금제 조회 실패', e);
    }
  }

  // 2) 기존 숙박(table5)에서 (일차, 요금제)별 이전 값 매핑 → SP 가 갱신 대상을 식별
  const prevByDayPlan = {}; // { `${dayNo}-${planNo}`: { prevPlaceName, prevBigo } }
  try {
    const detail = await getDetail();
    const table5 = Array.isArray(detail?.table5) ? detail.table5 : [];
    table5.forEach((row) => {
      const day = Number(row?.일차 || 0);
      const planNo = row?.가격옵션번호 || row?.요금제번호;
      if (day > 0 && planNo != null && String(planNo).trim() !== '') {
        prevByDayPlan[`${day}-${String(planNo)}`] = {
          prevPlaceName: row?.일정명 || row?.장소명 || '',
          prevBigo: row?.일정내용 || row?.비고 || '',
        };
      }
    });
  } catch (e) {
    console.warn('기존 숙박 정보 조회 실패', e);
  }

  // 3) 요금제 목록을 조회하지 못한 경우 기본요금제(1) 폴백
  const fallbackPlans = [{ planNo: '1', planName: '기본요금제', resourceNo: '' }];
  const targetPlans = allPlanEntries.length > 0 ? allPlanEntries : fallbackPlans;

  // @선택자원번호가 단일 값이므로 자원별로 호출을 분리
  const plansByResource = {};
  targetPlans.forEach((p) => {
    const key = String(p.resourceNo || '');
    if (!plansByResource[key]) plansByResource[key] = [];
    plansByResource[key].push(p);
  });

  let savedDays = 0;
  for (const dayNoStr of hotelDays) {
    const dayNo = Number(dayNoStr);
    const hotelName = (pendingHotels[dayNoStr] || []).join(', ');
    if (!hotelName) continue;

    let daySaved = false;
    for (const resourceKey of Object.keys(plansByResource)) {
      const plansForCall = plansByResource[resourceKey].map((p) => {
        const prev = prevByDayPlan[`${dayNo}-${String(p.planNo)}`] || {};
        return {
          planNo: p.planNo,
          planName: p.planName || '기본요금제',
          prevPlaceName: prev.prevPlaceName || '',
          prevBigo: prev.prevBigo || '',
        };
      });

      try {
        await saveHotelItineraryEdits({
          productNo,
          itineraryTypeNo,
          dayNo,
          placeNo: null,
          placeName: hotelName,
          bigo: '',
          resourceNo: resourceKey,
          plans: plansForCall,
        });
        daySaved = true;
      } catch (e) {
        const names = plansForCall.map((p) => p.planName || p.planNo).join(', ');
        console.error(`${dayNo}일차 호텔 저장 실패 (요금제: ${names}):`, e);
      }
    }
    if (daySaved) savedDays += 1;
  }

  return savedDays;
};
